import { createClient } from '@supabase/supabase-js';

// A anon key é pública por natureza (vai no bundle). A segurança real está na
// RLS: sem perfil ativo em `profiles`, o banco não devolve linha nenhuma.
// Ver core/sql/policies/rls.sql.
//
// URL VAZIA CAI NA PRÓPRIA ORIGEM, **SÓ EM DEV** — é o arranjo do clientes/, e
// é o que faz `npm run dev` funcionar sem CORS: o proxy do vite.config
// encaminha /rest/v1, /auth/v1 e /imagens para a stack de dev na VPS (túnel SSH
// em 127.0.0.1:8080), então o navegador enxerga UMA origem só.
//
// EM PRODUÇÃO NÃO HÁ FALLBACK, DE PROPÓSITO. Lá a própria origem é
// `sistema.…`, que NÃO é a API (`api.…`): cair nela faria o app abrir bonito e
// conversar com o host errado em silêncio — exatamente o acidente que o
// cabeçalho de infra/agente/deploy.sh descreve (2026-08-06). Sem URL, o certo é
// acender a tela de diagnóstico. Por isso `configurado` volta a exigir as duas
// coisas, como era antes.
//
// `typeof window` porque os testes rodam em Node, sem DOM: ler window.location
// no topo do módulo derrubava 5 arquivos de teste na coleta.
const origem = typeof window === 'undefined' ? '' : window.location.origin;
const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)
  || (import.meta.env.DEV ? origem : '');
const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';

export const configurado = Boolean(url && anonKey);

if (!configurado) {
  console.warn(
    'Faltam VITE_SUPABASE_URL e/ou VITE_SUPABASE_ANON_KEY. Copie '
    + 'app/.env.example para app/.env.local — em dev, a URL fica vazia e a '
    + 'chave é a DEV_ANON_KEY do infra/.env.dev da VPS.',
  );
}

// O placeholder existe só porque createClient('') estoura: com `configurado`
// falso o App nem chega a renderizar tela que use este cliente.
const urlDoCliente = url || 'http://localhost:8080';
const chaveDoCliente = anonKey || 'sem-chave';

/**
 * Com que URL e chave o cliente abaixo nasceu. Existe para o cliente do banco
 * PESSOAL (src/pessoal/cliente.ts, ADR-008), que fala com o mesmo host sob
 * /pessoal/ e precisa desta MESMA configuração — uma segunda leitura do .env
 * seria uma segunda verdade, e um dia as duas apontariam para lugares
 * diferentes.
 */
export const configuracaoDaApi = { url: urlDoCliente, chaveAnon: chaveDoCliente } as const;

export const supabase = createClient(urlDoCliente, chaveDoCliente, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // O celular do balcão fica logado o dia inteiro; sessão em localStorage
    // evita relogin a cada venda.
    storageKey: 'serginho-auth',
  },
});

/** Traduz erro do PostgREST/Postgres para algo que o vendedor entenda. */
export function mensagemDeErro(erro: unknown): string {
  if (!erro) return 'Erro desconhecido.';
  const e = erro as { message?: string; code?: string; details?: string };
  // Mensagem vazia renderizaria um alerta em branco — pior que erro nenhum,
  // porque o vendedor vê que algo falhou sem saber o quê.
  const bruto = e.message?.trim() || e.details?.trim() || String(erro);
  if (!bruto || bruto === '[object Object]') return 'Erro desconhecido.';

  if (/Invalid login credentials/i.test(bruto)) return 'E-mail ou senha incorretos.';
  if (/Email not confirmed/i.test(bruto)) return 'E-mail ainda não confirmado.';
  if (/Failed to fetch|NetworkError/i.test(bruto)) return 'Sem conexão com o servidor. Verifique a internet.';
  if (/Acesso negado/i.test(bruto)) return bruto.replace(/^.*Acesso negado:?\s*/i, 'Acesso negado: ');
  if (/Estoque insuficiente/i.test(bruto)) return bruto;
  if (/stock_nao_negativo/i.test(bruto)) return 'Estoque insuficiente para essa quantidade.';
  if (e.code === '23505') {
    if (/products_sku_key/.test(bruto)) return 'Já existe uma peça com esse código interno (SKU).';
    if (/codigo_barras/.test(bruto)) return 'Já existe uma peça com esse código de barras.';
    // O índice único de CPF é o que impede o histórico do cliente de se partir
    // em dois cadastros — a mensagem tem que mandar PROCURAR, não tentar de novo.
    if (/customers_cpf/.test(bruto)) {
      return 'Já existe um cliente com esse CPF/CNPJ. Procure por ele na busca em vez de cadastrar de novo.';
    }
    // O slug do fornecedor sai do nome (slugDeFornecedor): a colisão que o
    // banco acusa é de NOME, e é isso que a mensagem precisa dizer.
    if (/suppliers_slug/.test(bruto)) {
      return 'Já existe um fornecedor com esse nome. Abra o que já está cadastrado em vez de criar outro.';
    }
    if (/suppliers_cnpj/.test(bruto)) {
      return 'Já existe um fornecedor com esse CNPJ.';
    }
    return 'Esse registro já existe.';
  }
  if (e.code === '23503') return 'Registro relacionado não encontrado.';
  if (e.code === '42501' || /permission denied|row-level security/i.test(bruto)) {
    return 'Seu perfil não tem permissão para esta ação.';
  }
  return bruto;
}
